import {TouchEvent, useCallback, useEffect, useRef, WheelEvent} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {useSimpleDialogIsOpen} from '../../../../../../common/useSimpleDialogIsOpen.hook';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {useViewerMenuHandler} from './useViewerMenuHandler';

let wheelTriggered = false;

export function useVideoViewerEvent(isShortsDrama: boolean) {
  const isSimpleDialogOpen = useSimpleDialogIsOpen();
  const {isSeeking} = useTypedSelector(state => ({
    isSeeking: state.videoViewer.isSeeking,
  }));
  const {isMobile} = requestContext.requestInfo.display;
  const {handleMoveNextProduct, handleMovePrevProduct} = useViewerMenuHandler({
    isMobile,
    isShortsDrama,
  });

  const touchStartPosition = useRef<{x: number | null; y: number | null}>({
    x: null,
    y: null,
  });

  const onTouchStartHandler = useCallback((event: TouchEvent) => {
    touchStartPosition.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  }, []);

  const onTouchEndHandler = useCallback(
    (event: TouchEvent) => {
      if (
        touchStartPosition.current.y === null ||
        touchStartPosition.current.x === null
      ) {
        return;
      }

      const touchEndPosition = {
        x: event.changedTouches[0].clientX,
        y: event.changedTouches[0].clientY,
      };

      const threshold = window.innerHeight * 0.17; // 민감도를 조정하는 임계값

      const deltaY = touchStartPosition.current.y - touchEndPosition.y;
      const deltaX = touchStartPosition.current.x - touchEndPosition.x;

      // Y축 이동이 X축 이동보다 클 때만 동작
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        if (deltaY > threshold) {
          handleMoveNextProduct();
        }

        if (deltaY < -threshold) {
          handleMovePrevProduct();
        }
      }

      /**
       * 이전화/다음화 이동 시 작품판매정지/이용권구매팝업 등 Dialog 가 뜬 경우에
       * DialogMain 컴포넌트의 useStopPropagationOnScroll에서 touchstart 이벤트 버블링을 막고 있어
       * touchStart 이벤트가 발생하지 않아 touchStartPosition를 초기화를 해줍니다.
       */
      touchStartPosition.current = {x: null, y: null};
    },
    [handleMovePrevProduct, handleMoveNextProduct]
  );

  const onWheelHandler = useCallback(
    (event: WheelEvent) => {
      if (wheelTriggered || event.deltaY === 0) {
        return;
      }

      wheelTriggered = true;

      if (event.deltaY < 0) {
        handleMovePrevProduct();
      }

      if (event.deltaY > 0) {
        handleMoveNextProduct();
      }

      // 스로틀링 플래그 초기화 (2초 후)
      setTimeout(() => {
        wheelTriggered = false;
      }, 2000);
    },
    [handleMovePrevProduct, handleMoveNextProduct]
  );

  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        handleMoveNextProduct();
      }
    },
    [handleMoveNextProduct]
  );

  const onKeyUpHandler = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        handleMovePrevProduct();
      }
    },
    [handleMovePrevProduct]
  );

  useEffect(() => {
    // simpleDialog가 열려있거나 시크바를 조작중인 경우 뷰어에서 발생하는 이벤트는 무시합니다.
    if (!isShortsDrama || isSimpleDialogOpen || isSeeking) {
      return;
    }

    const eventHandlers = [
      {event: 'wheel', handler: onWheelHandler},
      {event: 'keyup', handler: onKeyUpHandler},
      {event: 'keydown', handler: onKeyDownHandler},
      {event: 'touchstart', handler: onTouchStartHandler},
      {event: 'touchend', handler: onTouchEndHandler},
    ];

    eventHandlers.forEach(({event, handler}) => {
      if (!event || !handler) {
        return;
      }
      // @TODO: 타입 이슈로 부득이하게 다음과 같이 사용하였습니다. 개선 수정이 필요합니다.
      window.addEventListener(event, handler as unknown as EventListener);
    });

    return () => {
      eventHandlers.forEach(({event, handler}) => {
        // @TODO: 타입 이슈로 부득이하게 다음과 같이 사용하였습니다. 개선 수정이 필요합니다.
        window.removeEventListener(event, handler as unknown as EventListener);
      });
    };
  }, [
    onWheelHandler,
    onKeyUpHandler,
    onKeyDownHandler,
    onTouchStartHandler,
    onTouchEndHandler,
    isShortsDrama,
    isSimpleDialogOpen,
    isSeeking,
  ]);
}
